import { useEffect } from "react";
import { Box, Button, Stack, Text, Title } from "@mantine/core";
import Request from "../common/Request.jsx";
import { RegistrationTracker } from "./RegistrationTracker";
import { OnboardingFooter, REGISTRATION_STEPS } from "./onboardingChrome";
import { ArrowRightIcon } from "./onboardingIcons";

const CONTROL_HEIGHT = 52;

type OnboardingEventName = "threshold_viewed" | "handoff_to_onboarding";

function resolveEventsUrl(): string {
  const resolved =
    typeof window !== "undefined" &&
    (
      window as unknown as {
        Artcloud?: {
          url?: {
            vendor?: { onboarding?: { events?: { post?: () => string } } };
          };
        };
      }
    ).Artcloud?.url?.vendor?.onboarding?.events?.post?.();
  return resolved || "/api/onboarding/events";
}

function emitOnboardingEvent(
  eventName: OnboardingEventName,
  isArtistReferral: boolean,
): void {
  // Fire-and-forget: analytics must never block the funnel or surface errors.
  // A rejected beacon promise must be caught here, otherwise it becomes an
  // unhandled rejection — try/catch only guards the synchronous path.
  try {
    const pending = Request.post(resolveEventsUrl(), {
      EventName: eventName,
      IsArtistReferral: isArtistReferral,
    });
    if (pending && typeof pending.catch === "function") {
      pending.catch(() => {
        /* swallow — analytics is best-effort */
      });
    }
  } catch (_) {
    /* swallow synchronous failures */
  }
}

export interface WelcomeScreenProps {
  isArtistReferral: boolean;
  onGetStarted: () => void;
}

export function WelcomeScreen({
  isArtistReferral,
  onGetStarted,
}: WelcomeScreenProps) {
  useEffect(() => {
    emitOnboardingEvent("threshold_viewed", isArtistReferral);
  }, [isArtistReferral]);

  function handleGetStarted() {
    emitOnboardingEvent("handoff_to_onboarding", isArtistReferral);
    onGetStarted();
  }

  return (
    <Box
      style={{
        gridColumn: "2 / 3",
        gridRow: "1 / -1",
        display: "flex",
        flexDirection: "column",
        minHeight: "90vh",
      }}
    >
      {/* No top-nav sign-in link here: the user has just created their account
          and is already signed in, so prompting them to sign in is contradictory. */}
      <Box
        style={{
          maxWidth: "600px",
          margin: "0 auto",
          padding: "2rem 1.5rem 0",
          width: "100%",
        }}
      >
        <RegistrationTracker steps={REGISTRATION_STEPS} activeStep={2} />
      </Box>

      <Box
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "2rem 1.5rem",
          width: "100%",
        }}
      >
        <Stack gap="md" maw="480px" mx="auto" w="100%">
          <Title
            order={1}
            fz="40px"
            fw={500}
            lh={1.15}
            data-testid="screen-heading"
            style={{ letterSpacing: "-0.02em", margin: "0 0 4px" }}
          >
            Welcome to ArtCloud.
          </Title>
          <Text size="md" c="gray.7" data-testid="screen-subheading">
            Your account is created. To get started, we'd love to know you a
            little better.
          </Text>

          <Button
            type="button"
            fullWidth
            h={CONTROL_HEIGHT}
            mt="lg"
            fz="15px"
            fw={400}
            rightSection={<ArrowRightIcon />}
            onClick={handleGetStarted}
            data-testid="get-started-button"
          >
            Get started
          </Button>
        </Stack>
      </Box>

      <OnboardingFooter />
    </Box>
  );
}
